<template>

  <div>
    <modal
      :title="$t('titles.hours')"
      :submit="submit"
      :submit-label="$t('titles.apply')"
      ref="hoursModal"
    >

      <div class="d-flex justify-space-between mb-4">

        <v-btn-toggle
          v-model="formState.weekdays"
          multiple
          mandatory
          variant="outlined"
          divided
          density="compact"
        >
          <v-btn value="sun">
            {{ $t('enums.weekdays_initial.sun') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.sun')" />
          </v-btn>
          <v-btn value="mon">
            {{ $t('enums.weekdays_initial.mon') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.mon')" />
          </v-btn>
          <v-btn value="tue">
            {{ $t('enums.weekdays_initial.tue') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.tue')" />
          </v-btn>
          <v-btn value="wed">
            {{ $t('enums.weekdays_initial.wed') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.wed')" />
          </v-btn>
          <v-btn value="thu">
            {{ $t('enums.weekdays_initial.thu') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.thu')" />
          </v-btn>
          <v-btn value="fri">
            {{ $t('enums.weekdays_initial.fri') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.fri')" />
          </v-btn>
          <v-btn value="sat">
            {{ $t('enums.weekdays_initial.sat') }}
            <v-tooltip activator="parent" location="bottom" :text="$t('enums.weekdays.sat')" />
          </v-btn>
        </v-btn-toggle>

        <v-btn-toggle variant="outlined" divided density="compact" :model-value="activeWeekdayButton" @update:model-value="() => false">
          <v-btn value="weekdays" @click="formState.weekdays = ['mon', 'tue', 'wed', 'thu', 'fri']">
            M–F
            <v-tooltip activator="parent" location="bottom" :text="$t('actions.weekdays')" />
          </v-btn>
          <v-btn value="weekends" @click="formState.weekdays = ['sun', 'sat']">
            S–S
            <v-tooltip activator="parent" location="bottom" :text="$t('actions.weekends')" />
          </v-btn>
          <v-btn value="all_days" @click="formState.weekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']">
            All
            <v-tooltip activator="parent" location="bottom" :text="$t('actions.all_days')" />
          </v-btn>
        </v-btn-toggle>

      </div>

      <v-row>

        <v-col>
          <o-switch
            :label="$t('titles.open_24_hours')"
            v-model="formState.open24Hours"
            hide-details
          />
        </v-col>

        <v-col>
          <o-switch
            :label="$t('titles.closed')"
            v-model="formState.closed"
            hide-details
          />
        </v-col>

      </v-row>

      <v-row :style="{ visibility: !formState.open24Hours && !formState.closed ? 'visible' : 'hidden'}">

        <v-col>
          <o-text-field
            :label="$t('titles.open')"
            type="time"
            v-model="formState.openLocal"
            :rules="formRules.openLocal"
          >
            <template v-slot:append>
              <v-btn icon @click="formState.openLocal = '00:00'">
                <v-icon icon="$midnight" />
                <v-tooltip activator="parent" location="bottom" :text="$t('actions.midnight')" />
              </v-btn>
            </template>
          </o-text-field>
        </v-col>

        <v-col>
          <o-text-field
            :label="$t('titles.close')"
            type="time"
            v-model="formState.closeLocal"
            :rules="formRules.closeLocal"
          >
            <template v-slot:append>
              <v-btn icon @click="formState.closeLocal = '00:00'">
                <v-icon icon="$midnight" />
                <v-tooltip activator="parent" location="bottom" :text="$t('actions.midnight')" />
              </v-btn>
            </template>
          </o-text-field>
        </v-col>

      </v-row>

    </modal>
  </div>    

</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// validation
import { required } from '../../../validation'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import type { LocationHoursModalResult, LocationHoursMode } from './types'
import Modal from '../../Modal.vue'

const { t } = useI18n()
const hoursModal = useTemplateRef<typeof Modal>('hoursModal')

interface Props {
  weekdays: string[]
  mode: LocationHoursMode
  openLocal: null | string
  closeLocal: null | string
}

const props = defineProps<Props>()

/**
 * Form
 */

interface FormState {
  weekdays: string[]
  open24Hours: boolean
  closed: boolean
  openLocal: null | string
  closeLocal: null | string
}

const formState = reactive<FormState>({
  weekdays: [],
  open24Hours: false,
  closed: false,
  openLocal: null,
  closeLocal: null,
})

const timeRequired = (value: any) => {
  if (
    formState.open24Hours ||
    formState.closed ||
    value !== null
  ) {
    return true
  }
  return t('validations.required')
}

const formRules = {
  weekdays: [required()], // kinda redundant; the mandatory prop ensures this
  openLocal: [
    timeRequired,
    (value: any) => {
      if (formState.open24Hours || formState.closed || value === null) {
        return true
      }
      if (formState.closeLocal && formState.closeLocal !== '00:00' && value >= formState.closeLocal) {
        return t('validations.must_be_before_close')
      }
      return true
    }
  ],
  closeLocal: [
    timeRequired,
    (value: any) => {
      if (formState.open24Hours || formState.closed || value === null) {
        return true
      }
      if (formState.openLocal && value !== '00:00' && value <= formState.openLocal) {
        return t('validations.must_be_after_open')
      }
      return true
    },
  ],
}

/**
 * Initialization
 */

onBeforeMount(() => {
  formState.weekdays = props.weekdays
  formState.open24Hours = props.mode === 'open24hours'
  formState.closed = props.mode === 'closed'
  // trim seconds
  formState.openLocal = props.openLocal ? props.openLocal.substring(0, 5) : null
  formState.closeLocal = props.closeLocal ? props.closeLocal.substring(0, 5) : null
})

const activeWeekdayButton = computed(() => {

  if (formState.weekdays.length === 7) {
    return 'all_days'
  }

  if (
    formState.weekdays.length === 5 &&
    ['mon', 'tue', 'wed', 'thu', 'fri'].every((weekday) => formState.weekdays.includes(weekday))
  ) {
    return 'weekdays'
  }

  if (
    formState.weekdays.length === 2 &&
    ['sun', 'sat'].every((weekday) => formState.weekdays.includes(weekday))
  ) {
    return 'weekends'
  }

  return null

})

// "Open 24 hours" and "Closed" are mutually exclusive

watch(() => formState.open24Hours, (open24Hours) => {
  if (open24Hours) {
    formState.closed = false
  }
})

watch(() => formState.closed, (closed) => {
  if (closed) {
    formState.open24Hours = false
  }
})

watch([() => formState.openLocal, () => formState.closeLocal], () => {
  if (formState.openLocal === '00:00' && formState.closeLocal === '00:00') {
    formState.open24Hours = true
  }
})

/**
 * Submission
 */

async function submit() {

  if (!hoursModal.value) {
    return
  }

  await hoursModal.value.modalForm.validate()

  if (!hoursModal.value.modalForm.isValid) {
    return
  }

  let output: LocationHoursModalResult

  if (
    formState.open24Hours ||
    (!formState.open24Hours && !formState.closed && formState.openLocal === '00:00' && formState.closeLocal === '00:00')
  ) {
    output = {
      weekdays: formState.weekdays,
      mode: 'open24hours',
      openLocal: null,
      closeLocal: null,
    }
  } else if (formState.closed) {
    output = {
      weekdays: formState.weekdays,
      mode: 'closed',
      openLocal: null,
      closeLocal: null,
    }
  } else {
    output = {
      weekdays: formState.weekdays,
      mode: 'open',
      openLocal: formState.openLocal + ':00',
      closeLocal: formState.closeLocal + ':00',
    }
  }

  closeDialog(output)

}

</script>
